import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Card, Col, Descriptions, Flex, Row, Space } from 'antd';

import { cpf, cnpj } from 'cpf-cnpj-validator';
import financialService from 'v2/services/financial';

import { CAIXA_ECONOMICA_ID, PersonalDocumentType } from '../../form';
import type { NewBankAccount } from '../../form';

type DataDisplayProps = {
  values: NewBankAccount;
  onPreviewClick: (fieldName: string) => void;
};

function DataDisplay({ values, onPreviewClick }: DataDisplayProps) {
  const [banks] = useObservable(() => financialService.getBanks(), []);

  const isDocumentCpf = cpf.isValid(values.document_number);

  function getDocumentName() {
    if (values.document_image_type === PersonalDocumentType.RG) return 'RG';
    if (values.document_image_type === PersonalDocumentType.CNH) return 'CNH';
    return 'Identidade profissional';
  }

  if (!banks) return null;

  const selectedBank = banks?.find(bank => bank.id === values.bank_id);
  const bankName = selectedBank?.value.replace(/[0-9]/g, '').trim();

  return (
    <Row className='-mx-2 mt-4 mb-6' gutter={[16, 16]}>
      <Col span={24}>
        <Descriptions title='Dados pessoais' bordered column={1}>
          <Descriptions.Item label={<span data-testid='name-label'>Nome</span>}>
            <span data-testid='name-value'>{values.name}</span>
          </Descriptions.Item>
          <Descriptions.Item label={<span data-testid='document-label'>{isDocumentCpf ? 'CPF' : 'CNPJ'}</span>}>
            <span data-testid='document-value'>
              {isDocumentCpf ? cpf.format(values.document_number) : cnpj.format(values.document_number)}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Col>
      {(values.document_image_front || values.document_image_back) && (
        <Col span={24}>
          <Card title='Documentos'>
            <Space direction='vertical' size='middle'>
              <span data-testid='personal-document-label'>{getDocumentName()}</span>
              <Flex wrap gap='small'>
                <Button id='document-image-front-preview-btn' onClick={() => onPreviewClick('document_image_front')}>
                  {values.document_image_type === PersonalDocumentType.RG && 'Visualizar frente RG'}
                  {values.document_image_type === PersonalDocumentType.CNH && 'Visualizar CNH'}
                  {values.document_image_type === PersonalDocumentType.PROFESSIONAL &&
                    'Visualizar frente Identidade Profissional'}
                </Button>

                {[PersonalDocumentType.RG, PersonalDocumentType.PROFESSIONAL].includes(values.document_image_type) && (
                  <Button id='document-image-back-preview-btn' onClick={() => onPreviewClick('document_image_back')}>
                    {values.document_image_type === PersonalDocumentType.RG && 'Visualizar verso RG'}
                    {values.document_image_type === PersonalDocumentType.PROFESSIONAL &&
                      'Visualizar verso Identidade Profissional'}
                  </Button>
                )}
              </Flex>
            </Space>
          </Card>
        </Col>
      )}
      {values.selfie_video && (
        <Col span={24}>
          <Card title='Vídeo'>
            <Space direction='vertical' size='middle'>
              <span>Vídeo selfie</span>
              <Button id='video-preview-btn' onClick={() => onPreviewClick('selfie_video')}>
                Visualizar vídeo
              </Button>
            </Space>
          </Card>
        </Col>
      )}
      <Col span={24}>
        <Descriptions title='Conta bancária' bordered column={1}>
          <Descriptions.Item label='Tipo'>
            <span data-testid='account-type-label'>{values.type === 'CC' ? 'Conta corrente' : 'Conta poupança'}</span>
          </Descriptions.Item>
          <Descriptions.Item label='Banco'>
            <span data-testid='bank-name-value'>{bankName}</span>
          </Descriptions.Item>
          <Descriptions.Item label='Dados'>
            <Space direction='vertical' size='small' data-testid='bank-data-value'>
              <span>Agência: {values.agency}</span>
              {values.agency_digit && <span>Dígito da agência: {values.agency_digit}</span>}
              <span>Conta: {values.account}</span>
              <span>Dígito da conta: {values.account_digit}</span>
              {values.bank_id === CAIXA_ECONOMICA_ID && <span>Operação: {values.operation_id}</span>}
            </Space>
          </Descriptions.Item>
          {values.bank_receipt_image && (
            <Descriptions.Item label='Comprovante'>
              <Button id='bank-receipt-preview-btn' onClick={() => onPreviewClick('bank_receipt_image')}>
                Visualizar comprovante
              </Button>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
    </Row>
  );
}

export { DataDisplay };
